// Jobs feature screen (task 3.6) — Req 3.
//
// On-site work capture. Wired to the Phase 2 jobs + photos handlers:
//   GET  /jobs?status=<status>              list by status (across clients)
//   GET  /clients/{id}/jobs                 a client's jobs
//   POST /clients/{id}/jobs                 create a job
//   GET  /clients/{id}/jobs/{jobId}         detail
//   PUT  /clients/{id}/jobs/{jobId}         edit (incl. status)
//   POST /clients/{id}/jobs/{jobId}/photos  presign a photo upload (Req 3.3)
//
// A job belongs to a client AND one of that client's sites (Req 3.1), so the
// router's single id segment packs both ids (see packJobId/unpackJobId). The jobs
// list has no "all" endpoint, so it queries each status and groups the results,
// annotating each with its client's name (loaded once).
//
// Photos (Req 3.3): captured straight from the phone camera. We POST the intended
// content type to get a short-lived presigned S3 PUT URL, then PUT the image bytes
// DIRECTLY to the private bucket (not through the API). The bucket is private with
// no presigned-GET endpoint in this phase, so the detail screen shows how many
// photos are attached rather than rendering them.

import { api } from '../api.js';
import {
  createJobOrQueue, listQueuedJobs, flushQueuedJobs, isOnline, QUEUE_CHANGED_EVENT,
} from '../offlineSync.js';
import {
  h, mount, runAsync, emptyView, routeToHash,
  screenEl, card, listRow, detailList, sectionHeading, muted,
  field, makeInput, makeTextarea, makeSelect, errorSlot, setError,
  button, linkButton, buttonRow, repeatableList, runAction, goTo,
  packJobId, unpackJobId, statusBadge, shortDate, toNum,
  JOB_STATUSES, DEFAULT_JOB_STATUS, REQUIRED_CERTIFICATES,
  setDraftSeed,
} from './ui.js';
import { jobGuidelineSection } from './guidelines.js';

const jobHash = (clientId, jobId) => routeToHash({ section: 'jobs', action: 'view', id: packJobId(clientId, jobId) });

// --- List (grouped by status) ----------------------------------------------

export function jobsListScreen() {
  const body = h('div');

  const load = () =>
    runAsync(
      body,
      async () => {
        const [clientsRes, queued, ...statusResults] = await Promise.all([
          api.get('/clients?includeArchived=true'),
          listQueuedJobs(),
          ...JOB_STATUSES.map((s) => api.get(`/jobs?status=${encodeURIComponent(s)}`)),
        ]);
        const clientName = new Map(((clientsRes && clientsRes.items) || []).map((c) => [c.id, c.name]));
        const groups = JOB_STATUSES.map((status, i) => ({
          status,
          jobs: (statusResults[i] && statusResults[i].items) || [],
        })).filter((g) => g.jobs.length);
        return { groups, clientName, queued };
      },
      ({ groups, clientName, queued }) => renderList(groups, clientName, queued),
      { loadingMessage: 'Loading jobs…' },
    );

  const renderList = (groups, clientName, queued) => {
    if (!groups.length && !queued.length) {
      return emptyView({
        message: 'No jobs captured yet.',
        actionLabel: 'New job',
        onAction: () => goTo({ section: 'jobs', action: 'new' }),
      });
    }
    return h('div', {}, [
      queued.length ? pendingSection(queued, clientName) : null,
      ...groups.map((g) =>
        h('div', { class: 'job-group' }, [
          h('div', { class: 'group-head' }, [statusBadge(g.status), h('span', { class: 'group-count', text: `${g.jobs.length}` })]),
          h(
            'div',
            { class: 'list' },
            g.jobs.map((j) =>
              listRow({
                href: jobHash(j.clientId, j.id),
                title: truncate(j.description, 60),
                subtitle: clientName.get(j.clientId) || 'Unknown client',
                meta: h('span', { class: 'list-row-date', text: shortDate(j.updatedAt) }),
              }),
            ),
          ),
        ]),
      ),
    ]);
  };

  // Pending captures saved offline (Req 3.6). Shown as non-tappable rows with a
  // "Pending" badge so the electrician can see their input is safe and will sync.
  // While online a "Sync now" button forces a flush; the online/queue-changed
  // listeners below re-render the list once items drain.
  const pendingSection = (queued, clientName) => {
    const rows = queued.map((item) =>
      h('div', { class: 'list-row pending-row' }, [
        h('div', { class: 'list-row-main' }, [
          h('span', { class: 'list-row-title', text: truncate((item.payload && item.payload.description) || 'Job note', 60) }),
          h('span', { class: 'list-row-sub', text: clientName.get(item.clientId) || 'Unknown client' }),
        ]),
        h('span', { class: 'list-row-meta' }, [statusBadge('pending')]),
      ]),
    );
    const actions = [];
    if (isOnline()) actions.push(button('Sync now', { small: true, onclick: () => flushQueuedJobs() }));
    return h('div', { class: 'job-group pending-group' }, [
      h('div', { class: 'group-head' }, [
        h('span', { class: 'group-title', text: 'Waiting to sync' }),
        h('span', { class: 'group-count', text: `${queued.length}` }),
      ]),
      muted(
        isOnline()
          ? 'Saved on this device — syncing to the server.'
          : "Saved on this device — you're offline. These sync automatically when your connection returns.",
      ),
      h('div', { class: 'list' }, rows),
      actions.length ? buttonRow(actions) : null,
    ]);
  };

  // Keep the pending state live: reload when the queue changes (an item queued or
  // synced) and try a flush when connectivity returns. The listeners self-remove
  // once this screen is no longer on the page (its body detaches on navigation),
  // so they don't leak or update a discarded screen.
  const onQueueChanged = () => { if (body.isConnected) load(); else detach(); };
  const onOnline = () => { if (body.isConnected) flushQueuedJobs(); else detach(); };
  const detach = () => {
    window.removeEventListener(QUEUE_CHANGED_EVENT, onQueueChanged);
    window.removeEventListener('online', onOnline);
  };
  window.addEventListener(QUEUE_CHANGED_EVENT, onQueueChanged);
  window.addEventListener('online', onOnline);

  load();
  return screenEl('Jobs', body, {
    section: 'jobs',
    actions: linkButton('＋ New', routeToHash({ section: 'jobs', action: 'new' }), { variant: 'primary', small: true }),
  });
}

// --- Shared job field controls ---------------------------------------------

// Materials (description / qty / unit) and labour (description / hours) editors,
// shared by create and edit. Returns the nodes plus readers.
function materialsEditor(initial = []) {
  const makeRow = (m, remove) => {
    const desc = makeInput({ value: m.description || '', placeholder: 'Material' });
    const qty = makeInput({ type: 'number', value: m.quantity ?? '', placeholder: 'Qty', inputmode: 'decimal', step: 'any', min: '0' });
    qty.classList.add('narrow');
    const unit = makeInput({ value: m.unit || '', placeholder: 'Unit' });
    unit.classList.add('narrow');
    const el = h('div', { class: 'mini-row' }, [desc, qty, unit, button('×', { small: true, onclick: remove, 'aria-label': 'Remove material' })]);
    const read = () => {
      const description = desc.value.trim();
      if (!description) return null;
      return { description, quantity: qty.value === '' ? 1 : toNum(qty.value), unit: unit.value.trim() };
    };
    return { el, read };
  };
  return repeatableList({ initial, makeRow, addLabel: 'Add material' });
}

function labourEditor(initial = []) {
  const makeRow = (l, remove) => {
    const desc = makeInput({ value: l.description || '', placeholder: 'Labour (e.g. first fix)' });
    const hours = makeInput({ type: 'number', value: l.hours ?? '', placeholder: 'Hours', inputmode: 'decimal', step: 'any', min: '0' });
    hours.classList.add('narrow');
    const el = h('div', { class: 'mini-row' }, [desc, hours, button('×', { small: true, onclick: remove, 'aria-label': 'Remove labour' })]);
    const read = () => {
      const hasHours = hours.value !== '';
      const description = desc.value.trim();
      if (!description && !hasHours) return null;
      return { description, hours: toNum(hours.value) };
    };
    return { el, read };
  };
  return repeatableList({ initial, makeRow, addLabel: 'Add labour' });
}

const certOptions = () => [{ value: '', label: 'None / not yet determined' }, ...REQUIRED_CERTIFICATES.map((c) => ({ value: c, label: c }))];
const statusOptions = () => JOB_STATUSES.map((s) => ({ value: s, label: s }));

// --- Create ----------------------------------------------------------------

export function jobNewScreen() {
  const body = h('div');

  runAsync(
    body,
    () => api.get('/clients'),
    (res) => renderForm((res && res.items) || []),
    { loadingMessage: 'Loading clients…' },
  );

  const renderForm = (clients) => {
    if (!clients.length) {
      return emptyView({
        message: 'Add a client before capturing a job.',
        actionLabel: 'New client',
        onAction: () => goTo({ section: 'clients', action: 'new' }),
      });
    }

    const clientSelect = makeSelect(
      [{ value: '', label: 'Select a client…' }, ...clients.map((c) => ({ value: c.id, label: c.name }))],
      { value: '' },
    );
    const siteSelect = makeSelect([{ value: '', label: 'Select a client first' }], { value: '' });

    const siteRef = { el: siteSelect };
    const populateSites = () => {
      const client = clients.find((c) => c.id === clientSelect.value);
      const sites = (client && client.sites) || [];
      const opts = sites.length
        ? sites.map((s) => ({ value: s.id, label: s.label ? `${s.label} — ${s.address}` : s.address }))
        : [{ value: '', label: 'This client has no sites' }];
      const fresh = makeSelect(opts, { value: opts[0].value });
      // Replace whichever select is currently live (not the original, which is
      // orphaned after the first swap) so repeated client changes keep working.
      siteRef.el.replaceWith(fresh);
      siteRef.el = fresh;
    };
    clientSelect.addEventListener('change', populateSites);

    const descInput = makeTextarea({ rows: 4, required: true, placeholder: 'What does the job involve?' });
    const notesInput = makeTextarea({ rows: 2, placeholder: 'Notes' });
    const statusSelect = makeSelect(statusOptions(), { value: DEFAULT_JOB_STATUS });
    const certSelect = makeSelect(certOptions(), { value: '' });
    const materials = materialsEditor([]);
    const labour = labourEditor([]);
    const err = errorSlot();
    const submit = button('Create job', { variant: 'primary' });

    submit.addEventListener('click', async () => {
      const clientId = clientSelect.value;
      const siteId = siteRef.el.value;
      const description = descInput.value.trim();
      if (!clientId) return setError(err, 'Please choose a client.');
      if (!siteId) return setError(err, 'Please choose a site for this job.');
      if (!description) return setError(err, 'Please describe the job.');
      const payload = {
        siteId,
        description,
        notes: notesInput.value.trim(),
        status: statusSelect.value,
        requiredCertificate: certSelect.value || null,
        materials: materials.values(),
        labour: labour.values(),
      };
      // Create now, or queue in IndexedDB if offline / the POST fails on
      // connectivity, so the capture is never lost (Req 3.6).
      const result = await runAction(submit, () => createJobOrQueue({ clientId, payload }), {
        busyLabel: 'Creating…', errSlot: err,
      });
      if (!result) return; // a real error was shown in the error slot
      if (result.status === 'created' && result.job && result.job.id) {
        goTo(jobHash(clientId, result.job.id));
      } else if (result.status === 'queued') {
        // Offline: land on the jobs list, where the pending state is visible.
        goTo({ section: 'jobs' });
      }
    });

    return h('div', { class: 'form' }, [
      field('Client', clientSelect),
      field('Site', siteRef.el),
      field('Description', descInput),
      field('Notes', notesInput),
      field('Status', statusSelect),
      field('Required certificate', certSelect),
      sectionHeading('Materials'),
      materials.el,
      sectionHeading('Labour'),
      labour.el,
      err,
      buttonRow([submit, linkButton('Cancel', routeToHash({ section: 'jobs' }))]),
    ]);
  };

  return screenEl('New job', body, {
    section: 'jobs',
    backHref: routeToHash({ section: 'jobs' }),
    backLabel: 'Jobs',
  });
}

// --- Detail (view + edit + photo capture) ----------------------------------

export function jobDetailScreen(packedId) {
  const ids = unpackJobId(packedId);
  if (!ids) {
    return screenEl('Job', emptyView({ message: 'That job link is invalid.' }), {
      section: 'jobs', backHref: routeToHash({ section: 'jobs' }), backLabel: 'Jobs',
    });
  }
  const { clientId, jobId } = ids;
  const body = h('div');

  const refresh = () =>
    runAsync(
      body,
      () => Promise.all([
        api.get(`/clients/${encodeURIComponent(clientId)}/jobs/${encodeURIComponent(jobId)}`),
        api.get(`/clients/${encodeURIComponent(clientId)}`).catch(() => null),
      ]),
      ([job, client]) => renderView(job, client),
      { loadingMessage: 'Loading job…' },
    );

  const renderView = (job, client) => {
    const site = client && (client.sites || []).find((s) => s.id === job.siteId);
    const wrap = h('div');

    const materialRows = (job.materials || []).map((m) =>
      h('div', { class: 'detail-row' }, [
        h('span', { class: 'detail-label', text: `${m.quantity}${m.unit ? ` ${m.unit}` : ''}` }),
        h('span', { class: 'detail-value', text: m.description }),
      ]),
    );
    const labourRows = (job.labour || []).map((l) =>
      h('div', { class: 'detail-row' }, [
        h('span', { class: 'detail-label', text: `${l.hours} h` }),
        h('span', { class: 'detail-value', text: l.description || 'Labour' }),
      ]),
    );

    const editBtn = button('Edit', {});
    editBtn.addEventListener('click', () => mount(body, renderEdit(job, client)));

    mount(wrap, h('div', {}, [
      card([
        detailList([
          ['Client', client ? client.name : clientId],
          ['Site', site ? (site.label ? `${site.label} — ${site.address}` : site.address) : job.siteId],
          ['Status', statusBadge(job.status)],
          ['Certificate', job.requiredCertificate],
          ['Updated', shortDate(job.updatedAt)],
        ]),
      ], { title: 'Job' }),
      card([h('p', { class: 'job-description', text: job.description }), job.notes ? h('p', { class: 'muted', text: job.notes }) : null], { title: 'Description' }),
      materialRows.length ? card(materialRows, { title: 'Materials' }) : null,
      labourRows.length ? card(labourRows, { title: 'Labour' }) : null,
      photosBlock(job),
      // Guideline review + flags stored against this job (Req 5.6).
      jobGuidelineSection(clientId, jobId),
      buttonRow([
        editBtn,
        draftButton('✨ Draft quote (AI)', 'quotes', clientId, jobId, job.description),
        draftButton('✨ Draft invoice (AI)', 'invoices', clientId, jobId, job.description),
      ]),
    ]));
    return wrap;
  };

  // A button that seeds the AI-draft form with this client/job/description and
  // navigates to the New quote / New invoice screen (the router can't carry the
  // extra context, so we stash a one-shot seed — see ui.setDraftSeed).
  const draftButton = (label, section, cId, jId, description) => {
    const b = button(label, { variant: 'primary' });
    b.addEventListener('click', () => {
      setDraftSeed({ section, clientId: cId, jobId: jId, description });
      goTo({ section, action: 'new' });
    });
    return b;
  };

  // Photo capture (Req 3.3): a phone-camera file input; on pick we presign, PUT
  // the bytes straight to S3, then refresh so the count updates.
  const photosBlock = (job) => {
    const count = (job.photoKeys || []).length;
    const err = errorSlot();
    const status = h('p', { class: 'muted' });

    const fileInput = h('input', {
      class: 'visually-hidden', type: 'file', accept: 'image/*', capture: 'environment',
      id: 'job-photo-input',
    });
    const addBtn = button('📷 Add photo', {});
    addBtn.addEventListener('click', () => fileInput.click());

    fileInput.addEventListener('change', async () => {
      const file = fileInput.files && fileInput.files[0];
      if (!file) return;
      setError(err, '');
      const contentType = file.type || 'image/jpeg';
      await runAction(addBtn, async () => {
        status.textContent = 'Uploading…';
        // 1) presign against the job (also records the key on the job)
        const presign = await api.post(
          `/clients/${encodeURIComponent(clientId)}/jobs/${encodeURIComponent(jobId)}/photos`,
          { contentType },
        );
        // 2) PUT the image bytes directly to the private bucket (no auth header;
        //    the presigned URL carries its own signature and pins Content-Type).
        const putRes = await fetch(presign.uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': contentType },
          body: file,
        });
        if (!putRes.ok) throw new Error(`Upload failed (${putRes.status}).`);
      }, { busyLabel: 'Uploading…', errSlot: err });
      status.textContent = '';
      fileInput.value = '';
      refresh();
    });

    const names = (job.photoKeys || []).map((k) => h('li', { class: 'photo-item', text: k.split('/').pop() }));

    return card([
      muted(count ? `${count} photo${count === 1 ? '' : 's'} attached.` : 'No photos yet.'),
      names.length ? h('ul', { class: 'photo-list' }, names) : null,
      buttonRow([addBtn]),
      fileInput,
      status,
      err,
    ], { title: 'Photos' });
  };

  const renderEdit = (job, client) => {
    const sites = (client && client.sites) || [];
    const siteSelect = sites.length
      ? makeSelect(sites.map((s) => ({ value: s.id, label: s.label ? `${s.label} — ${s.address}` : s.address })), { value: job.siteId })
      : makeSelect([{ value: job.siteId, label: job.siteId }], { value: job.siteId });
    const descInput = makeTextarea({ value: job.description, rows: 4, required: true });
    const notesInput = makeTextarea({ value: job.notes || '', rows: 2 });
    const statusSelect = makeSelect(statusOptions(), { value: job.status });
    const certSelect = makeSelect(certOptions(), { value: job.requiredCertificate || '' });
    const materials = materialsEditor(job.materials || []);
    const labour = labourEditor(job.labour || []);
    const err = errorSlot();
    const save = button('Save changes', { variant: 'primary' });
    const cancel = button('Cancel', {});
    cancel.addEventListener('click', () => refresh());

    save.addEventListener('click', async () => {
      const description = descInput.value.trim();
      if (!description) return setError(err, 'Please describe the job.');
      const payload = {
        siteId: siteSelect.value,
        description,
        notes: notesInput.value.trim(),
        status: statusSelect.value,
        requiredCertificate: certSelect.value || null,
        materials: materials.values(),
        labour: labour.values(),
      };
      const updated = await runAction(save, () => api.put(`/clients/${encodeURIComponent(clientId)}/jobs/${encodeURIComponent(jobId)}`, payload), {
        busyLabel: 'Saving…', errSlot: err,
      });
      if (updated) refresh();
    });

    return h('div', { class: 'form' }, [
      field('Site', siteSelect),
      field('Description', descInput),
      field('Notes', notesInput),
      field('Status', statusSelect),
      field('Required certificate', certSelect),
      sectionHeading('Materials'),
      materials.el,
      sectionHeading('Labour'),
      labour.el,
      err,
      buttonRow([save, cancel]),
    ]);
  };

  refresh();
  return screenEl('Job', body, {
    section: 'jobs',
    backHref: routeToHash({ section: 'jobs' }),
    backLabel: 'Jobs',
  });
}

function truncate(text, max) {
  const s = String(text || '');
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}
